import jsPDF from 'jspdf';
import 'jspdf-autotable';

// Helper function to convert number to Roman numeral
const toRomanNumeral = (num) => {
  if (num === 0) return "0";
  if (num === 1) return "I";
  if (num === 2) return "II";
  if (num === 3) return "III";
  if (num === 4) return "IV";
  return num.toString();
};

// Helper function to extract division number from division text
const extractDivisionNumber = (divisionText) => {
  if (!divisionText) return null;
  // Match "Division I", "Division II", "Division III", "Division IV", "Division 0"
  // IMPORTANT: Check IV before I to avoid matching "I" from "IV"
  const romanMatch = divisionText.match(/Division\s+(IV|III|II|I|0)/);
  if (romanMatch) {
    const roman = romanMatch[1];
    if (roman === "IV") return 4;
    if (roman === "III") return 3;
    if (roman === "II") return 2;
    if (roman === "I") return 1;
    if (roman === "0") return 0;
  }
  // Fallback for numeric divisions
  const numMatch = divisionText.match(/Division\s+(\d+)/);
  return numMatch ? parseInt(numMatch[1]) : null;
};

// Helper function to convert division text to Roman numeral format with points
const convertDivisionToRoman = (divisionText) => {
  if (!divisionText) return '';
  
  // Check if already in Roman format with points: "Division I • 12" or "Division I • 12.0"
  const romanMatch = divisionText.match(/Division\s+(IV|III|II|I|0)\s*[•·]\s*([\d\.]+)/);
  if (romanMatch) {
    const divisionRoman = romanMatch[1];
    const points = romanMatch[2];
    const cleanPoints = points.replace(/\.0$/, '');
    return `Div ${divisionRoman}.${cleanPoints}`;
  }
  
  // Check if already in Roman format without points: "Division I"
  const simpleRomanMatch = divisionText.match(/Division\s+(IV|III|II|I|0)(?!\s*[•·])/);
  if (simpleRomanMatch) {
    // Try to extract points from the end of the string if available
    const pointsMatch = divisionText.match(/(\d+\.?\d*)$/);
    if (pointsMatch) {
      const points = pointsMatch[1].replace(/\.0$/, '');
      return `Div ${simpleRomanMatch[1]}.${points}`;
    }
    return `Div ${simpleRomanMatch[1]}`;
  }
  
  // Try numeric format with points: "Division 1 • 12" or "Division 1 • 12.0"
  const numericMatch = divisionText.match(/Division\s+(\d+)\s*[•·]\s*([\d\.]+)/);
  if (numericMatch) {
    const divNum = parseInt(numericMatch[1]);
    const points = numericMatch[2];
    const cleanPoints = points.replace(/\.0$/, '');
    return `Div ${toRomanNumeral(divNum)}.${cleanPoints}`;
  }
  
  // Try numeric format without bullet: "Division 1 12" or "Division 1 . 12"
  const numericSpaceMatch = divisionText.match(/Division\s+(\d+)\s+\.?\s*(\d+\.?\d*)/);
  if (numericSpaceMatch) {
    const divNum = parseInt(numericSpaceMatch[1]);
    const points = numericSpaceMatch[2].replace(/\.0$/, '');
    return `Div ${toRomanNumeral(divNum)}.${points}`;
  }
  
  // Fallback: try just division number and extract any trailing numbers as points
  const simpleNumericMatch = divisionText.match(/Division\s+(\d+)/);
  if (simpleNumericMatch) {
    const divNum = parseInt(simpleNumericMatch[1]);
    // Try to find points elsewhere in the string
    const pointsMatch = divisionText.match(/(\d+\.?\d*)$/);
    if (pointsMatch && pointsMatch[1] !== simpleNumericMatch[1]) {
      const points = pointsMatch[1].replace(/\.0$/, '');
      return `Div ${toRomanNumeral(divNum)}.${points}`;
    }
    return `Div ${toRomanNumeral(divNum)}`;
  }
  
  return divisionText;
};

const generateClassResultsPDF = (
  groupedResults,
  className,
  examinationType,
  classTitle,
  marksView = '100'
) => {
  const isMarksOutOf100 = marksView === '100';
  /** PDF mirrors Class_Results: division only when exporting % (100) scale */
  const showDivisionInPdf = isMarksOutOf100;

  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // ====== IMPROVED HEADER FOR A4 PRINTING ======
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16); // Slightly smaller for A4
  doc.text("ST. MARY'S JUNIOR SEMINARY - VISIGA", pageWidth / 2, 12, {
    align: "center",
  });

  doc.setFontSize(12);
  doc.setFont("helvetica", "normal");
  doc.text("SEMINARIAN CLASS RESULTS", pageWidth / 2, 18, {
    align: "center",
  });

  // Display Class Title in header if available
  if (classTitle && classTitle !== "N/A") {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Class Title: ${classTitle}`, pageWidth / 2, 24, {
      align: "center",
    });
  }

  // ====== CLASS INFORMATION ======
  // Adjust y position based on whether class title is shown
  let y = classTitle && classTitle !== "N/A" ? 32 : 28; // Adjusted spacing for A4
  doc.setDrawColor(41, 128, 185);
  doc.setLineWidth(0.5);
  doc.line(15, y - 4, pageWidth - 15, y - 4);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11); // Smaller for A4
  doc.text("CLASS RESULTS SUMMARY", 15, y);

  y += 7;
  doc.setFontSize(10); // Smaller for A4

  const currentDate = new Date().toLocaleDateString();
  const info = [
    ["Class Name:", className || "N/A"],
    ["Class Title:", classTitle || "N/A"],
    ["Examination:", examinationType || "N/A"],
    ["Marks scale:", isMarksOutOf100 ? "Out of 100 (%)" : "Out of 40"],
    ["Date Generated:", currentDate],
    ["Total Students:", (groupedResults?.length || 0).toString()],
  ];

  info.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, 20, y);
    doc.setFont("helvetica", "normal");
    doc.text(value || "N/A", 60, y);
    y += 6; // Tighter spacing for A4
  });

  // ---------- Utility ----------
  const checkColumnEmpty = (getValue) => {
    if (!groupedResults || groupedResults.length === 0) return true;
    
    return groupedResults.every(student => {
      const value = getValue(student);
      return !value || value === 'N/A' || value === '';
    });
  };

  // ====== CALCULATE DIVISION SUMMARY ======
  const divisionCounts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
  groupedResults?.forEach(student => {
    const divNum = extractDivisionNumber(student?.division);
    if (divNum !== null && divNum >= 0 && divNum <= 4) {
      divisionCounts[divNum] = (divisionCounts[divNum] || 0) + 1;
    }
  });

  const validStudentGpas = (groupedResults || [])
    .map((student) => student.gpa)
    .filter((gpa) => gpa !== null && gpa !== undefined && !Number.isNaN(Number(gpa)));
  const classGpa =
    validStudentGpas.length > 0
      ? validStudentGpas.reduce((sum, gpa) => sum + Number(gpa), 0) / validStudentGpas.length
      : null;
  const formatGpa = (gpa) => {
    if (gpa === null || gpa === undefined || Number.isNaN(Number(gpa))) return "N/A";
    return Number(gpa).toFixed(4);
  };

  // ====== DISPLAY DIVISION SUMMARY RIGHT AFTER CLASS INFO ======
  const isDivisionEmpty = checkColumnEmpty(s => s?.division);
  if (showDivisionInPdf && !isDivisionEmpty) {
    y += 8; // Add some space after class info
    
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("DIVISION SUMMARY", 15, y);
    
    y += 6;
    
    // Create table for division summary - horizontal layout
    const divisionSummaryHeaders = [
      "Division",
      "Division I",
      "Division II",
      "Division III",
      "Division IV",
      "Division 0",
      "Class GPA",
    ];
    const divisionSummaryData = [
      [
        "Number of Students",
        divisionCounts[1] || 0,
        divisionCounts[2] || 0,
        divisionCounts[3] || 0,
        divisionCounts[4] || 0,
        divisionCounts[0] || 0,
        formatGpa(classGpa),
      ],
    ];
    
    doc.autoTable({
      head: [divisionSummaryHeaders],
      body: divisionSummaryData,
      startY: y,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 8,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },
      columnStyles: {
        0: { cellWidth: 42, halign: "left", fontStyle: "bold" },
        1: { cellWidth: 30, halign: "center" },
        2: { cellWidth: 30, halign: "center" },
        3: { cellWidth: 30, halign: "center" },
        4: { cellWidth: 30, halign: "center" },
        5: { cellWidth: 30, halign: "center" },
        6: { cellWidth: 30, halign: "center" },
      },
    });
    
    // Update y position after the table
    y = doc.lastAutoTable.finalY + 5; // Space after division summary table
  }

  // ====== CALCULATE SUBJECT GRADE SUMMARY ======
  const subjectGradeCounts = {};
  const excludedSubjects = [
    "CHEMISTRY-1", "CHEMISTRY-2", "CHEMISTRY-3",
    "PHYSICS-1", "PHYSICS-2", "PHYSICS-3",
    "BIOLOGY-1", "BIOLOGY-2", "BIOLOGY-3",
    "HISTORY-1", "HISTORY-2",
    "GEOGRAPHY-1", "GEOGRAPHY-2",
    "MATHEMATICS-1", "MATHEMATICS-2",
    "ECONOMICS-1", "ECONOMICS-2",
  ];
  const excludedSubjectsUpper = excludedSubjects.map(s => s.toUpperCase());

  // Helper: subject has results if marks/value is present (including 0)
  const hasSubjectResults = (subj) => {
    if (!subj) return false;
    if (subj?.marks !== undefined && subj?.marks !== null) return true;
    if (subj?.final?.value !== undefined && subj?.final?.value !== null) return true;
    if (subj?.final?.rawValue !== undefined && subj?.final?.rawValue !== null) return true;
    if (subj?.final?.convertedValue !== undefined && subj?.final?.convertedValue !== null)
      return true;
    if (subj?.rawMarks !== undefined && subj?.rawMarks !== null) return true;
    if (subj?.convertedMarks !== undefined && subj?.convertedMarks !== null) return true;
    return false;
  };

  groupedResults?.forEach(student => {
    Object.entries(student?.subjects || {}).forEach(([subject, details]) => {
      const subjectUpper = subject.toUpperCase();
      if (excludedSubjectsUpper.includes(subjectUpper)) return;
      
      // Only count grades for subjects that have results
      if (!hasSubjectResults(details)) return;
      
      if (!subjectGradeCounts[subject]) {
        subjectGradeCounts[subject] = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0, S: 0 };
      }
      
      const grade = details?.grade ?? details?.final?.grade;
      if (grade && ['A', 'B', 'C', 'D', 'E', 'F', 'S'].includes(grade.toUpperCase())) {
        const gradeUpper = grade.toUpperCase();
        if (subjectGradeCounts[subject][gradeUpper] !== undefined) {
          subjectGradeCounts[subject][gradeUpper]++;
        }
      }
    });
  });

  const isIndexEmpty = checkColumnEmpty(s => s?.studentNumber);
  const isCombinationEmpty = checkColumnEmpty(s => s?.combination);
  const isDivisionEmptyForTable = !showDivisionInPdf || isDivisionEmpty;

  // ---------- Subjects ----------
  const subjectSet = new Set();
  groupedResults?.forEach(student =>
    Object.keys(student?.subjects || {}).forEach(subj => subjectSet.add(subj))
  );

  const subjectOrder = [
    'CIVICS', 'HISTORY','HISTORIA YA TZ NA MAADILI', 'GEOGRAPHY','BIBLE-KNOWLEDGE','KISWAHILI', 'ENGLISH LANGUAGE','ENGLISH',
      'PHYSICS', 'CHEMISTRY','BIOLOGY','ECONOMICS','BASIC MATHEMATICS','MATHEMATICS', 'GENERAL STUDIES', 'ADVANCED MATHEMATICS',
     'ACADEMIC COMMUNICATION', 'BUSINESS STUDIES' 
  ];

  const subjectNames = Array.from(subjectSet).sort((a, b) => {
    const indexA = subjectOrder.indexOf(a?.toUpperCase?.());
    const indexB = subjectOrder.indexOf(b?.toUpperCase?.());
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a?.localeCompare?.(b) || 0;
  });

  const formatMarkCell = (num) => {
    if (!Number.isFinite(num)) return '';
    return num % 1 === 0 ? String(num.toFixed(0)) : num.toFixed(1);
  };

  const getSubjectMarks = (subj) => {
    if (!subj) return '';
    if (isMarksOutOf100) {
      if (
        subj.final?.convertedValue !== undefined &&
        subj.final?.convertedValue !== null
      ) {
        return formatMarkCell(parseFloat(subj.final.convertedValue));
      }
      if (subj.convertedMarks !== undefined && subj.convertedMarks !== null) {
        return formatMarkCell(parseFloat(subj.convertedMarks));
      }
    } else {
      if (subj.final?.rawValue !== undefined && subj.final?.rawValue !== null) {
        return formatMarkCell(parseFloat(subj.final.rawValue));
      }
      if (subj.rawMarks !== undefined && subj.rawMarks !== null) {
        return formatMarkCell(parseFloat(subj.rawMarks));
      }
    }
    if (subj?.marks !== undefined && subj?.marks !== null) return subj.marks;
    if (subj?.final?.value !== undefined && subj?.final?.value !== null) return subj.final.value;
    return '';
  };

  // ---------- Subject Display Names ----------
  const subjectDisplayNames = subjectNames.map(name => {
    const upper = name?.toUpperCase?.() || '';
    if (upper === 'ADVANCED MATHEMATICS') return 'A-MATH';
    if (upper === 'ENGLISH LANGUAGE' ) return 'ENG-LANG';
    if (upper === 'HISTORIA YA TZ NA MAADILI') return 'HIST-TZ';
    if (upper === 'GENERAL STUDIES') return 'G/STUDIES';
    if (upper === 'ACADEMIC COMMUNICATION') return 'A/COMM';
    if (upper === 'GEOGRAPHY') return 'GEO';
    if (upper === 'HISTORY') return 'HIST';
    if (upper === 'PHYSICS') return 'PHY';
    if (upper === 'CHEMISTRY') return 'CHEM';
    if (upper === 'BIOLOGY') return 'BIO';
    if (upper === 'ECONOMICS') return 'ECO';
    if (upper === 'BASIC MATHEMATICS') return 'B-MATH';
    if (upper === 'BIBLE-KNOWLEDGE') return 'B-KNOW';
    if (upper === 'BUSINESS STUDIES') return 'B-STUDIES';
    if (upper === 'MATHEMATICS') return 'MATH';
    if (upper === 'KISWAHILI') return 'KISW';
    
    return name || '';
  });

  // ---------- Headers ----------
  const baseHeaders = ['No', 'Student Name'];
  if (!isIndexEmpty) baseHeaders.push('Index No');
  if (!isCombinationEmpty) baseHeaders.push('Comb');
  baseHeaders.push(...subjectDisplayNames);
  baseHeaders.push('Total', 'Avg');
  if (!isDivisionEmptyForTable) baseHeaders.push('Div');
  baseHeaders.push('Pos');

  // ---------- Table Rows ----------
  const tableData = (groupedResults || []).map((student, index) => {
    const rowData = [index + 1, student?.studentName || ''];
    if (!isIndexEmpty) rowData.push(student?.studentNumber || '');
    if (!isCombinationEmpty) rowData.push(student?.combination || '');

    subjectNames.forEach(subject => {
      const subj = student?.subjects?.[subject];
      rowData.push(getSubjectMarks(subj));
    });

    const totalMarks = student?.totalMarks || 0;
    rowData.push(totalMarks === 0 ? '' : totalMarks);
    
    let averageDisplay;
    if (student?.average === "Incomplete") {
      averageDisplay = "Incomplete";
    } else {
      const avgNum = typeof student?.average === 'number' ? student.average : parseFloat(student?.average || 0);
      averageDisplay = avgNum === 0 ? '' : avgNum.toFixed(1);
    }
    rowData.push(averageDisplay);
    
    if (!isDivisionEmptyForTable) {
      const division = student?.division;
      // Convert division to Roman numeral format
      const divisionDisplay = convertDivisionToRoman(division) || '';
      rowData.push(divisionDisplay);
    }
    
    rowData.push(student?.position || '');
    return rowData;
  });

  // ---------- OPTIMIZED COLUMN WIDTHS FOR A4 ----------
  const fixedWidths = { 
    'No': 8, 
    'Student Name': 26  // Slightly reduced to make space for division
  };
  
  if (!isIndexEmpty) fixedWidths['Index No'] = 16;
  if (!isCombinationEmpty) fixedWidths['Comb'] = 12;

  subjectDisplayNames.forEach(subject => {
    fixedWidths[subject] = 8; // Slightly wider for readability
  });

  fixedWidths['Total'] = 11;
  fixedWidths['Avg'] = 11;
  if (!isDivisionEmptyForTable) fixedWidths['Div'] = 20; // SIGNIFICANTLY INCREASED for full division text
  fixedWidths['Pos'] = 10;

  // Calculate total table width
  const totalTableWidth = Object.values(fixedWidths).reduce((sum, width) => sum + width, 0);
  
  // Adjust if table is too wide for A4
  const maxA4Width = 270; // Maximum width for A4 landscape
  if (totalTableWidth > maxA4Width) {
    const scaleFactor = maxA4Width / totalTableWidth;
    Object.keys(fixedWidths).forEach(key => {
      if (key !== 'Div') { // Don't scale division column
        fixedWidths[key] = Math.floor(fixedWidths[key] * scaleFactor);
      }
    });
  }

  // ---------- TABLE WITH A4 OPTIMIZATIONS ----------
  doc.autoTable({
    head: [baseHeaders],
    body: tableData,
    startY: y + 8, // Adjusted spacing
    margin: { left: 8, right: 8, top: 5, bottom: 15 }, // Better margins for A4
    tableWidth: 'auto',
    styles: {
      fontSize: 7, // Slightly larger for better print readability
      cellPadding: 1.5, // More padding for print
      overflow: 'linebreak',
      halign: 'center',
      valign: 'middle',
      lineColor: [0, 0, 0],
      lineWidth: 0.2, // Slightly thicker lines for print
    },
    headStyles: {
      fillColor: [41, 128, 185],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 7,
      minCellHeight: 8,
      cellPadding: 2,
    },
    bodyStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 7,
      minCellHeight: 6,
      cellPadding: 1.5,
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245], // Lighter gray for better print
    },
    columnStyles: {
      'No': { 
        halign: 'center', 
        cellWidth: fixedWidths['No'],
        fontStyle: 'bold'
      },
      'Student Name': { 
        halign: 'left', 
        cellWidth: fixedWidths['Student Name'],
        fontSize: 6.5 // Slightly smaller for long names
      },
      ...(isIndexEmpty ? {} : { 
        'Index No': { 
          cellWidth: fixedWidths['Index No'], 
          halign: 'center',
          fontSize: 6.5
        } 
      }),
      ...(isCombinationEmpty ? {} : { 
        'Comb': { 
          cellWidth: fixedWidths['Comb'], 
          halign: 'center',
          fontSize: 6.5
        } 
      }),
      ...subjectDisplayNames.reduce((acc, subject) => {
        acc[subject] = {
          halign: 'center',
          valign: 'middle',
          cellWidth: fixedWidths[subject],
          fontSize: 7,
        };
        return acc;
      }, {}),
      'Total': { 
        cellWidth: fixedWidths['Total'], 
        halign: 'center',
        fontStyle: 'bold'
      },
      'Avg': { 
        cellWidth: fixedWidths['Avg'], 
        halign: 'center',
        fontStyle: 'bold'
      },
      ...(!isDivisionEmptyForTable ? { 
        'Div': { 
          cellWidth: fixedWidths['Div'], 
          halign: 'center',
          fontStyle: 'bold',
          fontSize: 7,
          cellPadding: 2, // Extra padding for division column
          minCellWidth: 20, // Ensure minimum width
        } 
      } : {}),
      'Pos': { 
        cellWidth: fixedWidths['Pos'], 
        halign: 'center',
        fontStyle: 'bold'
      },
    },

    // ---------- Vertical subject headers ----------
    didParseCell: (data) => {
      if (data.section === 'head' && subjectDisplayNames.includes(data.column.raw)) {
        data.cell.text = [];
      }
    },

    didDrawCell: (data) => {
      if (data.section === 'head' && subjectDisplayNames.includes(data.column.raw)) {
        const { x, y, width, height } = data.cell;
        const text = data.column.raw;
        const centerX = x + width / 2;
        const centerY = y + height / 2;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6); // Slightly larger for print
        doc.setTextColor(255, 255, 255);
        doc.saveGraphicsState();
        doc.text(text, centerX, centerY, {
          angle: 90,
          align: 'center',
          baseline: 'middle'
        });
        doc.restoreGraphicsState();
      }

      // Handle division column with color coding - ALWAYS apply colors
      if (data.section === 'body' && data.column.raw === 'Div') {
        const divisionValue = data.cell.raw || '';
        const { x, y, width, height } = data.cell;
        
        // Clear any existing text
        data.cell.text = [];
        
        // Determine color based on division (Roman numerals)
        // IMPORTANT: Check in order from most specific (IV) to least specific (I)
        let fillColor = [255, 255, 255]; // Default white background
        let textColor = [0, 0, 0]; // Default black text
        
        if (divisionValue) {
          const divUpper = divisionValue.toUpperCase();
          
          // Check for Division IV (most specific first)
          if (divUpper.includes('DIV IV') || divUpper.startsWith('DIV IV')) {
            fillColor = [255, 200, 200]; // Light red for Division IV
            textColor = [180, 0, 0]; // Dark red text
          }
          // Check for Division III
          else if (divUpper.includes('DIV III') || divUpper.startsWith('DIV III')) {
            fillColor = [255, 235, 180]; // Light orange/yellow for Division III
            textColor = [180, 100, 0]; // Dark orange text
          }
          // Check for Division II
          else if (divUpper.includes('DIV II') || divUpper.startsWith('DIV II')) {
            fillColor = [200, 220, 255]; // Light blue for Division II
            textColor = [0, 80, 180]; // Dark blue text
          }
          // Check for Division 0
          else if (divUpper.includes('DIV 0') || divUpper.startsWith('DIV 0')) {
            fillColor = [240, 240, 240]; // Light gray for Division 0
            textColor = [100, 100, 100]; // Gray text
          }
          // Check for Division I (must be last to avoid matching I from II, III, IV)
          else if (divUpper.includes('DIV I') || divUpper.startsWith('DIV I')) {
            fillColor = [200, 255, 200]; // Light green for Division I
            textColor = [0, 120, 0]; // Dark green text
          }
        }
        
        // Always draw background color (even if white)
        doc.setFillColor(...fillColor);
        doc.rect(x, y, width, height, 'F');
        
        // Draw border for better cell definition
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        doc.rect(x, y, width, height, 'S');
        
        // Draw the division text
        if (divisionValue) {
          doc.setFontSize(7);
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...textColor);
          
          const textX = x + width / 2;
          const textY = y + height / 2;
          
          doc.text(divisionValue, textX, textY, {
            align: 'center',
            baseline: 'middle'
          });
        }
      }

      // Highlight low/high marks and handle incomplete averages (include 0 in display)
      if (data.section === 'body') {
        if (subjectDisplayNames.includes(data.column.raw)) {
          const marks = data.cell.raw;
          const hasNumericMark = marks !== undefined && marks !== null && marks !== '' && (typeof marks === 'number' || !isNaN(parseFloat(marks)));
          if (hasNumericMark) {
            const numMarks = typeof marks === 'number' ? marks : parseFloat(marks);
            const lowThreshold = isMarksOutOf100 ? 40 : 16;
            const highThreshold = isMarksOutOf100 ? 80 : 32;
            if (numMarks < lowThreshold && numMarks > 0) {
              doc.setFillColor(255, 240, 240);
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
            } else if (numMarks >= highThreshold) {
              doc.setFillColor(240, 255, 240);
              doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
            }

            doc.setFontSize(7);
            doc.setTextColor(0, 0, 0);
            const marksText = marks?.toString?.() || String(marks);
            doc.text(marksText, data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
              align: 'center',
              baseline: 'middle'
            });
          }
        }
        
        if (data.column.raw === 'Avg' && data.cell.raw === "Incomplete") {
          doc.setFillColor(255, 240, 240);
          doc.rect(data.cell.x, data.cell.y, data.cell.width, data.cell.height, 'F');
          
          doc.setFontSize(7);
          doc.setTextColor(255, 0, 0);
          doc.text("Incomplete", data.cell.x + data.cell.width / 2, data.cell.y + data.cell.height / 2, {
            align: 'center',
            baseline: 'middle'
          });
        }
      }
    },

    // ====== A4 PAGE MANAGEMENT ======
    willDrawPage: (data) => {
      data.table.height = data.table.height - 12; // More space for footer
    },

    didDrawPage: (data) => {
      const totalPages = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      
      // ====== IMPROVED FOOTER FOR A4 ======
      doc.setFontSize(8);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(100, 100, 100);

      const footerY = pageHeight - 10;

      doc.text(
        "St. Mary's Junior Seminary Visiga (SIMS) System",
        pageWidth / 2,
        footerY - 6,
        { align: "center" }
      );

      doc.text(
        `Page ${currentPage} of ${totalPages}`,
        pageWidth / 2,
        footerY,
        { align: "center" }
      );
    },

    margin: { top: 5, bottom: 25 }, // More bottom margin for footer
  });

  // ====== ADD SUMMARY SECTIONS ======
  const finalY = doc.lastAutoTable.finalY || pageHeight - 30;
  let summaryY = finalY + 5;

  // Note: Division Summary has been moved to appear right after Class Results Summary (above the table)

  // Subject Grade Summary - Only show if there are non-zero grade counts
  const subjectNamesForSummary = Object.keys(subjectGradeCounts).sort();
  
  // Check if all grade counts are zero
  const hasNonZeroGrades = subjectNamesForSummary.some(subject => {
    const counts = subjectGradeCounts[subject];
    return (counts.A || 0) + (counts.B || 0) + (counts.C || 0) + 
           (counts.D || 0) + (counts.E || 0) + (counts.F || 0) + 
           (counts.S || 0) > 0;
  });
  
  if (isMarksOutOf100 && subjectNamesForSummary.length > 0 && hasNonZeroGrades) {
    // Check if we need a new page
    if (summaryY > pageHeight - 40) {
      doc.addPage();
      summaryY = 20;
    }
    
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("SUBJECT GRADE SUMMARY (marks out of 100)", 15, summaryY);
    
    summaryY += 6;
    
    // Create table for subject grade summary
    const gradeSummaryHeaders = ["Subject", "A", "B", "C", "D", "E", "F", "S"];
    const gradeSummaryData = subjectNamesForSummary.map(subject => {
      const counts = subjectGradeCounts[subject];
      return [
        subject,
        counts.A || 0,
        counts.B || 0,
        counts.C || 0,
        counts.D || 0,
        counts.E || 0,
        counts.F || 0,
        counts.S || 0
      ];
    });
    
    doc.autoTable({
      head: [gradeSummaryHeaders],
      body: gradeSummaryData,
      startY: summaryY,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 6,
      },
      bodyStyles: {
        fontSize: 6,
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 40, halign: "left" }, // Subject name
      },
    });

    // SUBJECT AVERAGE table below SUBJECT GRADE SUMMARY
    summaryY = doc.lastAutoTable.finalY + 8;
    if (summaryY > pageHeight - 40) {
      doc.addPage();
      summaryY = 20;
    }

    // Horizontal layout: subjects as columns; Average / Students as rows
    const shortSubjectName = (name) => {
      const upper = name?.toUpperCase?.() || "";
      if (upper === "ADVANCED MATHEMATICS") return "A-MATH";
      if (upper === "ENGLISH LANGUAGE") return "ENG-LANG";
      if (upper === "HISTORIA YA TZ NA MAADILI") return "HIST-TZ";
      if (upper === "GENERAL STUDIES") return "G/STUDIES";
      if (upper === "ACADEMIC COMMUNICATION") return "A/COMM";
      if (upper === "GEOGRAPHY") return "GEO";
      if (upper === "HISTORY") return "HIST";
      if (upper === "PHYSICS") return "PHY";
      if (upper === "CHEMISTRY") return "CHEM";
      if (upper === "BIOLOGY") return "BIO";
      if (upper === "ECONOMICS") return "ECO";
      if (upper === "BASIC MATHEMATICS") return "B-MATH";
      if (upper === "BIBLE-KNOWLEDGE") return "B-KNOW";
      if (upper === "BUSINESS STUDIES") return "B-STUDIES";
      if (upper === "MATHEMATICS") return "MATH";
      if (upper === "KISWAHILI") return "KISW";
      return name || "";
    };

    const subjectAvgAverages = [];
    const subjectAvgCounts = [];
    const subjectAvgHeaders = subjectNamesForSummary.map((subject) => {
      let total = 0;
      let count = 0;

      groupedResults?.forEach((student) => {
        const details = student?.subjects?.[subject];
        if (!hasSubjectResults(details)) return;

        const marks = Number(
          details?.marks ??
            details?.final?.convertedValue ??
            details?.convertedMarks ??
            details?.final?.value
        );

        if (!Number.isNaN(marks) && marks >= 0) {
          total += marks;
          count += 1;
        }
      });

      subjectAvgAverages.push(count > 0 ? (total / count).toFixed(2) : "N/A");
      subjectAvgCounts.push(count);
      return shortSubjectName(subject);
    });

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("SUBJECT AVERAGE", 15, summaryY);
    summaryY += 6;

    doc.autoTable({
      head: [["Subject", ...subjectAvgHeaders]],
      body: [
        ["Average", ...subjectAvgAverages],
        ["Students", ...subjectAvgCounts],
      ],
      startY: summaryY,
      margin: { left: 15, right: 15 },
      styles: {
        fontSize: 6,
        cellPadding: 1.5,
        overflow: "linebreak",
        halign: "center",
        valign: "middle",
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        fontSize: 6,
      },
      bodyStyles: {
        fontSize: 6,
        textColor: 0,
      },
      columnStyles: {
        0: { cellWidth: 22, halign: "left", fontStyle: "bold" },
      },
    });
  }

  const fileName = `Class_Results_${className || 'Class'}_${examinationType || 'Exam'}.pdf`;
  doc.save(fileName);
};

export default generateClassResultsPDF;